// src/pages/KnowledgeArticleDetail.js
// Componente para visualizar un artículo específico de la base de conocimientos
// Permite ver el contenido completo y artículos relacionados
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Paper,
    Button,
    Divider,
    Chip,
    CircularProgress,
    Alert,
    Breadcrumbs,
    Link
} from '@mui/material';
import {
    Book as BookIcon,
    ArrowBack as ArrowBackIcon,
    Home as HomeIcon,
    Category as CategoryIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { get } from '../services/apiService';
import FiberLoader from '../components/common/FiberLoader';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';

const KnowledgeArticleDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [relatedArticles, setRelatedArticles] = useState([]);

    useEffect(() => {
        fetchArticleDetails();
    }, [id]);

    const fetchArticleDetails = async () => {
        try {
            const token = localStorage.getItem('clientToken');
            const response = await fetch(`/api/client/support/knowledge/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al obtener artículo');
            }

            const data = await response.json();
            setArticle(data);

            // Una vez que tenemos el artículo, podemos buscar artículos relacionados
            fetchRelatedArticles(data.category, data.tags);
        } catch (err) {
            console.error('Error fetching article:', err);
            setError(err.message || 'Error al cargar el artículo');
        } finally {
            setLoading(false);
        }
    };

    const fetchRelatedArticles = async (category, tags) => {
        try {
            const token = localStorage.getItem('clientToken');
            const response = await fetch(`/api/client/support/knowledge`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const allArticles = await response.json();

                // Filtrar artículos relacionados por categoría o tags
                const related = allArticles.filter(a =>
                    (a.id !== id) && // Excluir el artículo actual
                    (a.category === category ||
                        (a.tags && tags && a.tags.some(tag => tags.includes(tag))))
                ).slice(0, 3); // Tomar los 3 primeros

                setRelatedArticles(related);
            }
        } catch (err) {
            console.error('Error fetching related articles:', err);
        }
    };

    if (loading) {
        return (
            <FiberLoader text="Cargando artículo de ayuda..." subtext="Descargando guía de fibra óptica y soporte..." fullPage />
        );
    }

    if (error) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/client/support')}
                >
                    Volver a Soporte
                </Button>
            </Container>
        );
    }

    if (!article) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Alert severity="warning" sx={{ mb: 3 }}>
                    No se encontró el artículo solicitado.
                </Alert>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/client/support')}
                >
                    Volver a Soporte
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Breadcrumbs separator="›" aria-label="breadcrumb" sx={{ mb: 3 }}>
                <Link
                    color="inherit"
                    href="/client/dashboard"
                    underline="hover"
                    sx={{ display: 'flex', alignItems: 'center' }}
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Inicio
                </Link>
                <Link
                    color="inherit"
                    href="/client/support"
                    underline="hover"
                    sx={{ display: 'flex', alignItems: 'center' }}
                >
                    <InfoIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Soporte
                </Link>
                <Typography
                    sx={{ display: 'flex', alignItems: 'center' }}
                    color="text.primary"
                >
                    <BookIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Artículo
                </Typography>
            </Breadcrumbs>

            <Paper sx={{ p: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 4 }}>
                    <Typography variant="h4" component="h1" gutterBottom>
                        {article.title}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {article.category && (
                            <Chip
                                icon={<CategoryIcon />}
                                label={article.category}
                                color="primary"
                                variant="outlined"
                                size="small"
                                sx={{ mr: 1 }}
                            />
                        )}
                        <Typography variant="body2" color="textSecondary">
                            Actualizado: {format(new Date(article.updated_at), 'dd/MM/yyyy')}
                        </Typography>
                    </Box>

                    {article.tags && article.tags.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            {article.tags.map((tag, index) => (
                                <Chip
                                    key={index}
                                    label={tag}
                                    size="small"
                                    sx={{ mr: 0.5, mb: 0.5 }}
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                <Divider sx={{ mb: 4 }} />

                <Box sx={{ mb: 4 }}>
                    <Typography component="div" variant="body1">
                        <ReactMarkdown>
                            {article.content}
                        </ReactMarkdown>
                    </Typography>
                </Box>

                <Divider sx={{ mb: 4 }} />

                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        ¿Te ha resultado útil este artículo?
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button variant="outlined" color="success">
                            Sí, me ha ayudado
                        </Button>
                        <Button variant="outlined" color="error">
                            No, necesito más ayuda
                        </Button>
                    </Box>
                </Box>
            </Paper>

            {relatedArticles.length > 0 && (
                <Paper sx={{ p: 4 }}>
                    <Typography variant="h6" gutterBottom>
                        Artículos Relacionados
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {relatedArticles.map((related, index) => (
                        <Box key={index} sx={{ mb: index < relatedArticles.length - 1 ? 2 : 0 }}>
                            <Link
                                href={`/client/support/knowledge/${related.id}`}
                                underline="hover"
                                color="primary"
                                sx={{ display: 'block', mb: 0.5 }}
                            >
                                <Typography variant="subtitle1">
                                    {related.title}
                                </Typography>
                            </Link>
                            <Typography variant="body2" color="textSecondary" noWrap>
                                {related.content.substring(0, 100)}...
                            </Typography>
                            {index < relatedArticles.length - 1 && <Divider sx={{ mt: 2 }} />}
                        </Box>
                    ))}
                </Paper>
            )}

            <Box sx={{ mt: 3 }}>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/client/support')}
                >
                    Volver a la Base de Conocimientos
                </Button>
            </Box>
        </Container>
    );
};

export default KnowledgeArticleDetail;